import React, { useState } from 'react';
import useShoppingCart, { UseShoppingCart } from 'features/commerce/shopping-cart/useShoppingCart';
import Modal from 'shared/components/Modal';
import { Localizer } from 'services/Localizer';
import { AddToCartExceptionCode } from 'features/commerce/product-details/types';
import { AddToCartResponse } from 'services/UserData.Commerce';
import AddedToCartAlert from 'features/commerce/store/AddedToCartAlert';
import InlineAlert from 'shared/edit/InlineAlert';
import Spinner from 'shared/components/Spinner';
import { dispatchRocEvent } from 'shared/hooks/useRocEventHandler';
import ShoppingCartSVG from 'shared/components/svg/ShoppingCartSVG';
import { CompareResultObject } from 'shared/components/hawksearch/models/CompareResultObject';

export interface CompareProductAddToCartProps {
	compareResultItem: CompareResultObject;
}

export default function CompareProductAddToCart({ compareResultItem }: CompareProductAddToCartProps) {
	const productId = compareResultItem.DocId;
	const [modalOpen, setModalOpen] = useState<boolean>(true);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [addToCartResponse, setAddToCartResponse] = useState<AddToCartResponse | null>(null);

	const UseShoppingCartResult: UseShoppingCart = useShoppingCart();
	const {
		state: { errorMessage, errorType },
		actor: { addToCart },
	} = UseShoppingCartResult;

	const errorTitle =
		errorType === AddToCartExceptionCode.cartFull
			? Localizer('CartFull')
			: errorType === AddToCartExceptionCode.productOutOfStock
			? Localizer('OutOfStock')
			: errorType === AddToCartExceptionCode.productUnavailable
			? Localizer('ProductUnavailable')
			: Localizer('ErrorMessageGenericTitle');

	const handleAddToCart = async () => {
		try {
			if (!productId) {
				return;
			}

			setAddToCartResponse(null);
			setIsLoading(true);
			const response = await addToCart({
				itemId: productId,
				quantity: 1,
				productPricingLineId: null,
			});

			setModalOpen(true);
			setIsLoading(false);

			if (!response) {
				setAddToCartResponse(null);
				return;
			}

			dispatchRocEvent('roc-cart-summary-updated', { cartSummary: response.cartSummary });

			setAddToCartResponse(response);
		} catch (error) {
			setAddToCartResponse(null);
		}
	};

	return (
		<>
			<div className="hawk-results__add-to-cart">
				{isLoading ? (
					<div>
						<Spinner align="left" />
						{Localizer('Loading')}
					</div>
				) : (
					<button className="roc-btn" onClick={() => handleAddToCart()} key={productId}>
						<span>{Localizer('AddToCart')}</span>
						<ShoppingCartSVG />
					</button>
				)}
			</div>

			{addToCartResponse !== null ? (
				<Modal
					isOpen={modalOpen}
					renderTitle={() => Localizer('AddedToCart')}
					closeModal={() => setModalOpen(false)}
					className="addedToCartModal"
				>
					<AddedToCartAlert response={addToCartResponse} />
				</Modal>
			) : null}

			{errorMessage && (
				<Modal isOpen={modalOpen} renderTitle={() => errorTitle} closeModal={() => setModalOpen(false)}>
					<InlineAlert theme="danger" isTitleOnOwnRow message={<>{errorMessage}</>} />
				</Modal>
			)}
		</>
	);
}
